import React from "react";
import { compose } from "recompose";
import { withUserProfile } from "../../../../../userProfile/userProfileContext";
import { format } from "date-fns";
import reportConstants from "../constants";
import Events from "../../../../../utilities/EventEmitter";
import { utcToZonedTime } from "date-fns-tz";
import { withSnackbar } from "notistack";
import MESSAGES from "../../../../../appMessages";
import DriverPerformanceRenderer from "../components/ReportRenderer";
import { populateFormattedData, populateLinks } from "../util";
import { withAppConfig } from "../../../../../appConfigContext";
import { getDriverPerformanceData } from "./utils";
class DriverPerformanceRealtime extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      data: null,
      isLoading: false,
      isDataRefreshed: false,
      formattedFilteredData: null,
    };
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.userData.userProfile.terminal.id !==
      prevProps.userData.userProfile.terminal.id
    ) {
      this.getDriverPerformanceData();
    }
  }

  componentDidMount() {
    Events.on("RefreshDispatchStore", this.refreshReportData);
    Events.on("HandlePickupEvent", this.refreshReportData);
    Events.on("RefreshPickupsData", this.refreshReportData);
    Events.on("UpdateDriverRoute", this.updateDriverInfo);
    Events.on("UpdateDriverLoginStatus",this.updateDriverInfoHandler);
    Events.on("dispatchCanceled", this.updateDriverInfoHandler);
    Events.on("updateDriverGateinGateout",this.updateDriverInfoHandler)
    this.getDriverPerformanceData();
  }

  componentWillUnmount() {
    Events.off("RefreshDispatchStore", this.refreshReportData);
    Events.off("HandlePickupEvent", this.refreshReportData);
    Events.off("RefreshPickupsData", this.refreshReportData);
    Events.off("UpdateDriverRoute", this.updateDriverInfo);
    Events.off("UpdateDriverLoginStatus",this.updateDriverInfoHandler);
    Events.off("dispatchCanceled", this.updateDriverInfoHandler);
    Events.off("updateDriverGateinGateout",this.updateDriverInfoHandler)
  }

  refreshReportData = () => {
    this.getDriverPerformanceData([], true);
  };

  getTerminalDateTime = () => {
    return utcToZonedTime(
      new Date(),
      this.props.userData.userProfile.terminal.offset
    );
  };

  getCurrentDispatchDate = () => {
    return format(this.getTerminalDateTime(), reportConstants.APP_DATE_FORMAT);
  };

  updateDriverInfoHandler = ({ driverId }) => {
    this.updateDriverInfo(driverId);
  };

  updateDriverInfo = (driverId) => {
    const driverIdList = Array.isArray(driverId)
      ? driverId.map((d) => d.toUpperCase())
      : driverId
      ? [driverId.toUpperCase()]
      : [];
    const existingDrivers = this.state.data?.map((driver) => driver.driverId?.toUpperCase());

    // Filter out drivers who are existing in the current performance report only
    const filteredDriversToUpdate = driverIdList.filter((driverId) =>
      existingDrivers.includes(driverId)
    );

    // fetch Data for drivers with driverIds
    if (filteredDriversToUpdate.length > 0)
      this.getDriverPerformanceData(filteredDriversToUpdate, true);
  };

  handleError = () => {
    this.props.enqueueSnackbar(MESSAGES.DRIVER_PERFORMANCE.FETCH_DATA_FAILURE, {
      variant: "error",
    });
    this.setState({
      isLoading: false,
      data: null,
    });
  };

  getDriverPerformanceData = (driverIdList = [], isDataRefreshed = false) => {
    const { userData } = this.props;
    const driverIds = driverIdList?.filter(Boolean);
    !isDataRefreshed && this.setState({ isLoading: true, data: null });
    getDriverPerformanceData(
      userData.userProfile.terminal.code,
      this.getCurrentDispatchDate(),
      driverIds
    ).then(
      ({ status, data }) => {
        if (status && status >= 200 && status <= 400) {
          if (driverIds?.length) {
            // If GET api was called for particular list of drivers
            this.updateDriverDataByDriverIds(data, driverIds);
          } else {
            // If GET api was called for entire drivers in the report
            this.initializeReportData(data, isDataRefreshed);
          }
        } else {
          !isDataRefreshed && this.handleError();
        }
      },
      () => {
        !isDataRefreshed && this.handleError();
      }
    ).catch((_err) => {});
  };

  updateDriverDataByDriverIds = (driversData, driverIdList) => {
    const data = [...this.state.data];
    driverIdList.map((driverId) => {
      const indexToUpdate = data.findIndex(
        (driverData) => driverData.driverId?.toUpperCase() === driverId
      );
      if (driversData) {
        // If fetched data is avaiable, update the details of corresponding drivers
        const updatedDriverData = driversData.find(
          (driverData) => driverData.driverId?.toUpperCase() === driverId
        );
        data[indexToUpdate] = { ...updatedDriverData };
      } else {
        // If data is not present, remove the details of the particular driver from state.
        data.splice(indexToUpdate, 1);
      }
    });
    this.initializeReportData(data, true);
  };

  getFormattedReportData = (data) => {
    const formattedData = populateFormattedData({
      driversData: data,
      dispatchDate: this.getCurrentDispatchDate(),
      isRealtimeReport: true,
      showTrailerScac: this.props.configJSON.showTrailerScac,
    });
    const links = populateLinks(formattedData);
    return {
      tasks: formattedData,
      links: links,
    };
  };

  initializeReportData = (responseData, isDataRefreshed) => {
    // If responseData is null or [], set it as null
    const data = responseData?.length ? responseData : null;
    this.setState({
      data,
      formattedFilteredData: data ? this.getFormattedReportData(data) : null,
      isDataRefreshed,
      isLoading: false,
    });
  };

  updateFilteredData = (filteredData) => {
    this.setState({
      formattedFilteredData: this.getFormattedReportData(filteredData),
      isDataRefreshed: false,
    });
  };

  render() {
    const { isDataRefreshed, isLoading, data, formattedFilteredData } =
      this.state;
    return (
      <React.Fragment>
        <DriverPerformanceRenderer
          isLoading={isLoading}
          data={data}
          isDataRefreshed={isDataRefreshed}
          currentDispatchDate={this.getCurrentDispatchDate()}
          isRealtimeReport
          updateFilteredData={this.updateFilteredData}
          formattedFilteredData={formattedFilteredData}
          showBackButton={false}
        />
      </React.Fragment>
    );
  }
}
export default compose(
  withUserProfile,
  withSnackbar,
  withAppConfig,
)(DriverPerformanceRealtime);
